import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import PrivacyModeDropdown from './PrivacyModeDropdown';
import './MainAppV2.css';

const MainAppV2 = ({ user, onLogout, supabaseIntegration }) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [searchRadius, setSearchRadius] = useState(1000);
  const [placeType, setPlaceType] = useState('restaurant');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [interestedPlaces, setInterestedPlaces] = useState(new Set());
  const [mapType, setMapType] = useState('roadmap');
  const [markers, setMarkers] = useState([]);
  const [currentPrivacyMode, setCurrentPrivacyMode] = useState('public');

  const { profile } = supabaseIntegration;

  // Initialize privacy mode from profile
  useEffect(() => {
    if (profile?.default_privacy_mode) {
      setCurrentPrivacyMode(profile.default_privacy_mode);
    }
  }, [profile]);

  // Load user's current interests on component mount
  useEffect(() => {
    if (user && supabaseIntegration?.supabaseHelpers) {
      loadUserInterests();
    }
  }, [user, supabaseIntegration]);

  const loadUserInterests = async () => {
    try {
      console.log('Loading user interests for user:', user.sub);
      const { data: interests, error } = await supabaseIntegration.supabaseHelpers.getUserInterests(user.sub);
      console.log('getUserInterests response:', { interests, error });
      
      if (!error && interests) {
        const interestedPlaceIds = new Set(interests.map(interest => interest.place_id));
        console.log('Setting interested places:', interestedPlaceIds);
        setInterestedPlaces(interestedPlaceIds);
      } else if (error) {
        console.error('Error from getUserInterests:', error);
      }
    } catch (error) {
      console.error('Error loading user interests:', error);
    }
  };

  // Initialize Google Maps
  useEffect(() => {
    const initMap = () => {
      if (!window.google || !window.google.maps || !window.google.maps.Map) {
        setError('Google Maps API not fully loaded');
        return;
      }

      if (!mapRef.current) {
        setError('Map container not found');
        return;
      }

      try {
        const defaultLocation = { lat: 37.7749, lng: -122.4194 };
        
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center: defaultLocation,
          zoom: 15,
          mapTypeId: mapType,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          gestureHandling: 'greedy',
          styles: [
            {
              featureType: "poi.business",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.government",
              elementType: "labels", 
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.medical",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.school",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });

        setMap(mapInstance);
        setError(null);
        setIsInitializing(false);

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userPos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              setUserLocation(userPos);
              mapInstance.setCenter(userPos);
              
              new window.google.maps.Marker({
                position: userPos,
                map: mapInstance,
                title: 'Your Location',
                icon: {
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <!-- Outer pulsing ring -->
                      <circle cx="20" cy="20" r="18" fill="#3B82F6" fill-opacity="0.2" stroke="#3B82F6" stroke-width="2" stroke-opacity="0.4">
                        <animate attributeName="r" values="12;18;12" dur="2s" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" values="0.4;0.1;0.4" dur="2s" repeatCount="indefinite"/>
                      </circle>
                      <!-- Middle ring -->
                      <circle cx="20" cy="20" r="12" fill="#3B82F6" fill-opacity="0.3" stroke="#FFFFFF" stroke-width="2"/>
                      <!-- Inner dot -->
                      <circle cx="20" cy="20" r="6" fill="#3B82F6" stroke="#FFFFFF" stroke-width="2"/>
                      <!-- Center dot -->
                      <circle cx="20" cy="20" r="3" fill="#FFFFFF"/>
                    </svg>
                  `),
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 20), // Center the icon
                },
                zIndex: 1000, // Ensure it appears above other markers
              });
            },
            (error) => {
              console.warn('Geolocation error:', error);
            }
          );
        }
      } catch (error) {
        setError(`Failed to initialize map: ${error.message}`);
        setIsInitializing(false);
      }
    };

    if (!process.env.REACT_APP_GOOGLE_API_KEY) {
      setError('Google API key not configured');
      setIsInitializing(false);
      return;
    }

    if (!window.google || !window.google.maps) {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', initMap);
        existingScript.addEventListener('error', () => {
          setError('Failed to load Google Maps API');
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      window.initGoogleMaps = () => {
        initMap();
        delete window.initGoogleMaps;
      };
      
      script.onerror = () => {
        setError('Failed to load Google Maps API');
        delete window.initGoogleMaps;
      };
      
      document.head.appendChild(script);
    } else {
      initMap();
    }

    return () => {
      if (window.initGoogleMaps) {
        delete window.initGoogleMaps;
      }
    };
  }, []);

  // Update map type when mapType state changes
  useEffect(() => {
    if (map) {
      map.setMapTypeId(mapType);
    }
  }, [map, mapType]);

  // Search for nearby places
  const searchNearbyPlaces = async () => {
    if (!map) {
      setError('Maps not initialized');
      return;
    }

    setLoading(true);
    setError(null);
    setNearbyPlaces([]);

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    setMarkers([]);

    let latitude, longitude;
    if (userLocation) {
      latitude = userLocation.lat;
      longitude = userLocation.lng;
    } else {
      const center = map.getCenter();
      latitude = center.lat();
      longitude = center.lng();
    }
    
    const getIncludedTypes = (type) => {
      const typeMapping = {
        'restaurant': ['restaurant'],
        'cafe': ['cafe'],
        'bar': ['bar'],
        'food': ['restaurant', 'cafe', 'bakery', 'meal_takeaway'],
        'meal_takeaway': ['meal_takeaway'],
        'bakery': ['bakery']
      };
      return typeMapping[type] || ['restaurant'];
    };

    const requestBody = {
      includedTypes: getIncludedTypes(placeType),
      maxResultCount: 20,
      locationRestriction: {
        circle: {
          center: {
            latitude: latitude,
            longitude: longitude
          },
          radius: searchRadius
        }
      },
      languageCode: 'en'
    };

    try {
      const response = await fetch('https://places.googleapis.com/v1/places:searchNearby', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_API_KEY,
          'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.rating,places.priceLevel,places.types,places.id'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Places API error: ${response.status} - ${errorData}`);
      }

      const data = await response.json();
      
      if (data.places && data.places.length > 0) {
        setNearbyPlaces(data.places);
        
        // Create new markers and store them for cleanup
        const newMarkers = [];
        data.places.forEach((place, index) => {
          const isInterested = interestedPlaces.has(place.id);
          const marker = new window.google.maps.Marker({
            position: {
              lat: place.location.latitude,
              lng: place.location.longitude
            },
            map: map,
            title: place.displayName?.text || 'Unknown Place',
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2C9.58 2 6 5.58 6 10C6 16 14 26 14 26S22 16 22 10C22 5.58 18.42 2 14 2Z" fill="${isInterested ? '#10B981' : '#EF4444'}"/>
                  <circle cx="14" cy="10" r="3" fill="white"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(28, 28),
            },
          });

          // Enhanced click listener - opens place detail card
          marker.addListener('click', () => {
            setSelectedPlace(place);
            // Optional: Center map on selected place
            map.panTo({
              lat: place.location.latitude,
              lng: place.location.longitude
            });
          });

          newMarkers.push(marker);
        });
        
        setMarkers(newMarkers);
      } else {
        setNearbyPlaces([]);
      }
    } catch (error) {
      setError(`Places search failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-search on load and when filters change
  useEffect(() => {
    if (map && !isInitializing) {
      searchNearbyPlaces();
    }
  }, [map, placeType, searchRadius, isInitializing]);

  // Update marker colors when interests change
  useEffect(() => {
    if (markers.length > 0 && nearbyPlaces.length > 0) {
      updateMarkerColors(interestedPlaces);
    }
  }, [interestedPlaces]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.user-profile-section')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleShowInterest = async (place) => {
    const newInterestedPlaces = new Set(interestedPlaces);
    const isCurrentlyInterested = interestedPlaces.has(place.id);
    
    console.log('handleShowInterest called:', { 
      placeId: place.id, 
      placeName: place.displayName?.text, 
      isCurrentlyInterested 
    });
    
    try {
      if (isCurrentlyInterested) {
        // Remove interest
        console.log('Removing interest for place:', place.id);
        newInterestedPlaces.delete(place.id);
        const result = await supabaseIntegration.supabaseHelpers.removeUserInterest(user.sub, place.id);
        console.log('removeUserInterest result:', result);
      } else {
        // Add interest (privacy mode is handled globally)
        console.log('Adding interest for place:', place.id);
        newInterestedPlaces.add(place.id);
        
        const placeData = {
          place_id: place.id,
          place_name: place.displayName?.text || 'Unknown Place',
          place_address: place.formattedAddress || '',
          place_location: {
            lat: place.location.latitude,
            lng: place.location.longitude
          }
        };
        
        console.log('Calling addUserInterest with:', placeData);
        const result = await supabaseIntegration.supabaseHelpers.addUserInterest(
          user.sub, 
          placeData
        );
        console.log('addUserInterest result:', result);
      }
      
      setInterestedPlaces(newInterestedPlaces);
      
      // Update marker colors without calling Google Places API again
      updateMarkerColors(newInterestedPlaces);
      
    } catch (error) {
      console.error('Error updating interest:', error);
      // Revert the UI change if the database operation failed
      // The set will remain unchanged if there was an error
    }
  };

  // Helper function to update marker colors without API call
  const updateMarkerColors = (interestedPlaceIds) => {
    markers.forEach((marker, index) => {
      const place = nearbyPlaces[index];
      if (place) {
        const isInterested = interestedPlaceIds.has(place.id);
        marker.setIcon({
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2C9.58 2 6 5.58 6 10C6 16 14 26 14 26S22 16 22 10C22 5.58 18.42 2 14 2Z" fill="${isInterested ? '#10B981' : '#EF4444'}"/>
              <circle cx="14" cy="10" r="3" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(28, 28),
        });
      }
    });
  };

  const handlePrivacyModeChange = (newMode) => {
    setCurrentPrivacyMode(newMode);
    // The PrivacyModeDropdown component handles updating the database
  };

  const getPlaceTypeEmoji = (type) => {
    switch (type) {
      case 'restaurant': return '🍽️';
      case 'cafe': return '☕';
      case 'bar': return '🍺';
      case 'bakery': return '🥐';
      case 'meal_takeaway': return '🥡';
      default: return '🍽️';
    }
  };

  const getRadiusText = (radius) => {
    if (radius < 1000) return `${radius}m`;
    return `${radius / 1000}km`;
  };

  const handleViewProfile = () => {
    setShowProfileMenu(false);
    navigate(`/profile/${user.sub}`);
  };

  return (
    <div className="main-app-v2">
      {/* Floating Header */}
      <header className="floating-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">PopMates</div>
            <div className="tagline">What's poppin' near you?</div>
          </div>
          
          <div className="search-controls-inline">
            <div className="control-pill">
              <span className="control-emoji">{getPlaceTypeEmoji(placeType)}</span>
              <select value={placeType} onChange={(e) => setPlaceType(e.target.value)}>
                <option value="restaurant">Restaurants</option>
                <option value="cafe">Cafes</option>
                <option value="bar">Bars</option>
                <option value="food">Any Food</option>
                <option value="meal_takeaway">Takeaway</option>
                <option value="bakery">Bakeries</option>
              </select>
            </div>

            <div className="control-pill">
              <span className="control-emoji">📍</span>
              <select value={searchRadius} onChange={(e) => setSearchRadius(Number(e.target.value))}>
                <option value={500}>500m</option>
                <option value={1000}>1km</option>
                <option value={2000}>2km</option>
                <option value={5000}>5km</option>
              </select>
            </div>

            <button 
              onClick={searchNearbyPlaces} 
              disabled={!map || loading}
              className="refresh-pill"
            >
              {loading ? '🔄' : '🔍'}
            </button>

            <button 
              onClick={() => setMapType(mapType === 'roadmap' ? 'satellite' : 'roadmap')}
              disabled={!map}
              className="map-type-pill"
              title={`Switch to ${mapType === 'roadmap' ? 'satellite' : 'road'} view`}
            >
              {mapType === 'roadmap' ? '🛰️' : '🗺️'}
            </button>
          </div>

          <div className="header-right-section">
            {/* Privacy Mode Dropdown */}
            <PrivacyModeDropdown 
              user={user}
              profile={profile}
              supabaseIntegration={supabaseIntegration}
              onPrivacyModeChange={handlePrivacyModeChange}
            />
            
            {/* Notification Bell */}
            <NotificationBell supabaseIntegration={supabaseIntegration} />
            
            {/* User Profile Section */}
            <div className="user-profile-section">
              <div 
                className="user-profile"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <img 
                  src={user.picture} 
                  alt={`${user.name}'s avatar`}
                  className="user-avatar"
                />
                <span className="user-name">{user.given_name}</span>
                <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              
              {showProfileMenu && (
                <div className="profile-menu">
                  <button 
                    className="menu-item"
                    onClick={handleViewProfile}
                  >
                    👤 View Profile
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowUserProfile(true);
                      setShowProfileMenu(false);
                    }}
                  >
                    ℹ️ Account Info
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowSettings(true);
                      setShowProfileMenu(false);
                    }}
                  >
                    ⚙️ Settings
                  </button>
                  <button 
                    className="menu-item logout"
                    onClick={onLogout}
                  >
                    🚪 Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Integrated Layout */}
      <main className="app-main-integrated">
        <div className="map-container">
          {isInitializing && !error && (
            <div className="map-loading">
              <div className="loading-spinner"></div>
              <p>Loading map...</p>
            </div>
          )}
          {error && (
            <div className="map-error">
              <div className="error-icon">❌</div>
              <p>{error}</p>
            </div>
          )}
          <div ref={mapRef} className="map" style={{ display: (isInitializing || error) ? 'none' : 'block' }} />
        </div>

        {/* Floating Places Panel */}
        <div className="floating-places-panel">
          {/* Mobile Search Controls - only visible on mobile */}
          <div className="mobile-search-controls">
            <div className="control-pill">
              <span className="control-emoji">{getPlaceTypeEmoji(placeType)}</span>
              <select value={placeType} onChange={(e) => setPlaceType(e.target.value)}>
                <option value="restaurant">Restaurants</option>
                <option value="cafe">Cafes</option>
                <option value="bar">Bars</option>
                <option value="food">Any Food</option>
                <option value="meal_takeaway">Takeaway</option>
                <option value="bakery">Bakeries</option>
              </select>
            </div>

            <div className="control-pill">
              <span className="control-emoji">📍</span>
              <select value={searchRadius} onChange={(e) => setSearchRadius(Number(e.target.value))}>
                <option value={500}>500m</option>
                <option value={1000}>1km</option>
                <option value={2000}>2km</option>
                <option value={5000}>5km</option>
              </select>
            </div>

            <button 
              onClick={searchNearbyPlaces} 
              disabled={!map || loading}
              className="refresh-pill"
            >
              {loading ? '🔄' : '🔍'}
            </button>

            <button 
              onClick={() => setMapType(mapType === 'roadmap' ? 'satellite' : 'roadmap')}
              disabled={!map}
              className="map-type-pill"
              title={`Switch to ${mapType === 'roadmap' ? 'satellite' : 'road'} view`}
            >
              {mapType === 'roadmap' ? '🛰️' : '🗺️'}
            </button>
          </div>

          <div className="places-scroll">
            {loading && (
              <div className="loading-places">
                <div className="loading-spinner small"></div>
                <p>Finding places nearby...</p>
              </div>
            )}
            {nearbyPlaces.length === 0 && !loading && (
              <div className="no-places">
                <div className="no-places-emoji">🔍</div>
                <p>No places found nearby</p>
                <p>Try expanding your search radius</p>
              </div>
            )}
            
            {nearbyPlaces.map((place) => {
              const isInterested = interestedPlaces.has(place.id);
              return (
                <div 
                  key={place.id} 
                  className={`place-card-compact ${selectedPlace?.id === place.id ? 'selected' : ''}`}
                  onClick={() => setSelectedPlace(place)}
                >
                  <div className="place-main-info">
                    <h4>{place.displayName?.text || 'Unknown Place'}</h4>
                    <div className="place-meta-row">
                      {place.rating && (
                        <span className="place-rating">⭐ {place.rating}</span>
                      )}
                      {place.priceLevel && (
                        <span className="place-price">
                          {'$'.repeat(place.priceLevel)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button 
                    className={`interest-button-compact ${isInterested ? 'interested' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowInterest(place);
                    }}
                  >
                    {isInterested ? '✅' : '🍽️'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Place Detail */}
        {selectedPlace && (
          <div className="selected-place-detail">
            <div className="detail-header">
              <button 
                className="close-detail"
                onClick={() => setSelectedPlace(null)}
              >
                ×
              </button>
            </div>
            <div className="detail-content">
              <h3>{selectedPlace.displayName?.text || 'Unknown Place'}</h3>
              <p className="detail-address">{selectedPlace.formattedAddress}</p>
              <div className="detail-meta">
                {selectedPlace.rating && (
                  <span className="detail-rating">⭐ {selectedPlace.rating}</span>
                )}
                {selectedPlace.priceLevel && (
                  <span className="detail-price">
                    {'$'.repeat(selectedPlace.priceLevel)}
                  </span>
                )}
              </div>
              <div className="detail-actions">
                <button 
                  className={`interest-button-large ${interestedPlaces.has(selectedPlace.id) ? 'interested' : ''}`}
                  onClick={() => handleShowInterest(selectedPlace)}
                >
                  {interestedPlaces.has(selectedPlace.id) ? 
                    '✅ I\'m interested!' : 
                    '🍽️ Show interest'
                  }
                </button>
                <div className="interest-status">
                  {interestedPlaces.has(selectedPlace.id) ? 
                    `Looking for others who are interested... (expires in 60 min, ${currentPrivacyMode} visibility)` :
                    `Click to let others know you're thinking of going here (${currentPrivacyMode} visibility)`
                  }
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Profile Modal */}
      {showUserProfile && (
        <div className="modal-overlay" onClick={() => setShowUserProfile(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Your Profile</h2>
              <button 
                className="close-button"
                onClick={() => setShowUserProfile(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="profile-info">
                <img 
                  src={user.picture} 
                  alt={`${user.name}'s avatar`}
                  className="profile-avatar"
                />
                <div className="profile-details">
                  <div className="profile-field">
                    <label>Name:</label>
                    <span>{user.name}</span>
                  </div>
                  <div className="profile-field">
                    <label>Email:</label>
                    <span>{user.email}</span>
                  </div>
                  <div className="profile-field">
                    <label>First Name:</label>
                    <span>{user.given_name}</span>
                  </div>
                  <div className="profile-field">
                    <label>Last Name:</label>
                    <span>{user.family_name}</span>
                  </div>
                  <div className="profile-field">
                    <label>Email Verified:</label>
                    <span>{user.email_verified ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Settings</h2>
              <button 
                className="close-button"
                onClick={() => setShowSettings(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="settings-content">
                <div className="settings-message">
                  <div className="settings-icon">⚙️</div>
                  <h3>Settings Coming Soon</h3>
                  <p>No settings are available yet. Please check back later as we continue to build PopMates!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainAppV2; 